import React, { useEffect, useState } from 'react'

import history from 'utils/history'
import { useSelector } from 'react-redux'
import jwtDecode from 'jwt-decode'
import { Scopes } from 'utils/authUtils'
import { listUsers } from 'services/apiKYE'
import { toastifyAxiosError } from 'utils/errorReporter'
import { NoDataWrapper } from 'components/ApplicationLog/BuildFeed/styles'
import { Add, Edit, InfoOutlined, Remove } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import _ from 'lodash'
import { getUserPermissionIcon, USER_PERMISSION, USER_ROLE_FILTERS } from 'utils/constants/user'
import ConfigUserDialog from './ConfigUserDialog'
import { ROUTE_CONSTANTS } from '../../utils/constants/routeConstants'
import styles from './styles'

const UserManagement = () => {
  const classes = styles()
  const token = useSelector((state) => state.auth.access_token)
  const [openConfigUserDialog, setOpenConfigUserDialog] = useState(false)
  const [refreshUsersData, setRefreshUsersData] = useState(false)
  const [loading, setLoading] = useState(false)
  const [usersData, setUsersData] = useState({ items: [], total: 0, page: 1, size: 10 })
  const [filters, setFilters] = useState({ page: 1, name: '', user_role: USER_ROLE_FILTERS.all })
  const [selectedUser, setSelectedUser] = useState(null)

  const callListUsers = () => {
    const user_role = filters.user_role === USER_ROLE_FILTERS.all ? null : filters.user_role
    setLoading(true)
    listUsers(token, { ...filters, user_role: user_role })
      .then((res) => {
        setUsersData(res)
      })
      .catch((err) => {
        toastifyAxiosError(err, 'Ocorreu um erro ao listar os usuários')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const decoded = jwtDecode(token)
    if (decoded.scopes.includes(Scopes.USER_MANAGEMENT) === false) {
      history.push(ROUTE_CONSTANTS.HOME)
      history.go(0)
    }
  }, [token])

  useEffect(() => {
    callListUsers()
  }, [filters.user_role, filters.page, refreshUsersData])

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      callListUsers()
    }, 400)

    return () => clearTimeout(delayDebounceFn)
  }, [filters.name])

  const onClickAdd = () => {
    setSelectedUser({})
    setOpenConfigUserDialog(true)
  }

  const onCloseConfigUserDialog = () => {
    setOpenConfigUserDialog(false)
    setSelectedUser({})
  }

  const onSuccessConfigUser = () => {
    onCloseConfigUserDialog()
    setRefreshUsersData(!refreshUsersData)
  }

  const renderPermissions = (user) => {
    const { permissions } = user

    if (_.every(permissions, (permission) => !permission)) {
      return <Remove />
    }
    return (
      <React.Fragment>
        {_.map(permissions, (value, permission_name) =>
          value ? (
            <Tooltip arrow key={`tt-${permission_name}`} title={<h3>{USER_PERMISSION[permission_name.toUpperCase()]}</h3>}>
              <span style={{ margin: '0 2px' }}>
                {getUserPermissionIcon(permission_name.toUpperCase(), 'small')}
              </span>
            </Tooltip>
          ) : null,
        )}
      </React.Fragment>
    )
  }

  const renderActive = (user) => (
    <b style={{ color: user.enabled ? 'green' : 'red', fontWeight: 600 }}>{user.enabled ? 'Ativo' : 'Inativo'}</b>
  )

  const renderEditButton = (user) => {
    const onClickEdit = () => {
      setSelectedUser(user)
      setOpenConfigUserDialog(true)
    }

    return (
      <IconButton onClick={onClickEdit}>
        <Edit fontSize="small" />
      </IconButton>
    )
  }

  const renderFilters = () => (
    <div style={{ display: 'flex', gap: 20, margin: '10px 0', alignItems: 'end', justifyContent: 'start' }}>
      <TextField
        variant="standard"
        label="Filtrar por nome ou e-mail"
        fullWidth={true}
        value={filters.name}
        onChange={(ev) => setFilters({ ...filters, name: ev.target.value, page: 1 })}
      ></TextField>
      <FormControl style={{ width: 320 }}>
        <InputLabel variant="standard" htmlFor="user-role">
          Função
        </InputLabel>
        <Select
          labelId="user-role"
          id="user-role"
          label="Função"
          variant="standard"
          value={filters.user_role}
          onChange={(ev) => setFilters({ ...filters, user_role: ev.target.value, page: 1 })}
          fullWidth={true}
        >
          {_.map(USER_ROLE_FILTERS, (role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <div>
        <Button size="small" variant="outlined" onClick={onClickAdd} startIcon={<Add />}>
          Adicionar
        </Button>
      </div>
    </div>
  )

  return (
    <React.Fragment>
      <div className={classes.root}>
        <div className={classes.textContent}>
          <h2>Controle de acesso dos usuários</h2>
          <p>
            Nesta tela é possível visualizar, buscar e adicionar usuários do sistema, além de gerenciar permissões de
            acesso aos recursos.
          </p>
        </div>

        <div>
          <div style={{ display: 'flex', gap: 20, flexDirection: 'column', alignItems: 'center' }}>
            {renderFilters()}
            {loading ? (
              <div>
                <CircularProgress size={50} />
              </div>
            ) : !_.isEmpty(usersData?.items) ? (
              <table className={classes.dataTable}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Função</th>
                    <th>Permissões</th>
                    <th>Status</th>
                    <th>Editar</th>
                  </tr>
                </thead>

                <tbody>
                  {usersData?.items?.map((item) => (
                    <tr key={item.email}>
                      <td>{item.name}</td>
                      <td>{item.email}</td>
                      <td>{item.user_role}</td>
                      <td>{renderPermissions(item)}</td>
                      <td>{renderActive(item)}</td>
                      <td>{renderEditButton(item)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <NoDataWrapper>
                <span>
                  <InfoOutlined fontSize="inherit" />
                </span>
                <span>Nenhum usuário encontrado</span>
              </NoDataWrapper>
            )}
            {!_.isEmpty(usersData?.items) && <div>
              <Pagination
                count={Math.ceil(usersData.total / usersData.size)}
                page={usersData.page}
                onChange={(__, page) => setFilters({ ...filters, page: page })}
              />
            </div>}
          </div>
        </div>
      </div>

      <ConfigUserDialog
        userData={selectedUser}
        open={openConfigUserDialog}
        onClose={onCloseConfigUserDialog}
        onSuccess={onSuccessConfigUser}
      />
    </React.Fragment>
  )
}

UserManagement.propTypes = {}

export default UserManagement
